import { Head, Link } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Equipment {
  id: number
  display_name: string
  requires_odometer: boolean
}

interface Assignment {
  id: number
  assigned_at: string
  returned_at: string | null
  employee_name: string | null
  removed_by_name: string | null
  case_id: number | null
  case_name: string | null
  install_vehicle: string | null
  install_gps_location: string | null
  install_vehicle_location: string | null
  remove_vehicle_location: string | null
  start_odometer: number | null
  end_odometer: number | null
  odometer_usage: number | null
  billable_amount: number | null
  notes: string | null
}

interface Props {
  equipment: Equipment
  assignments: Assignment[]
}

export default function History({ equipment, assignments }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Equipment", href: "/equipment" },
    { title: equipment.display_name, href: `/equipment/${equipment.id}` },
    { title: "Case History", href: `/equipment/${equipment.id}/history` },
  ]

  const formatDateTime = (value: string | null) => {
    if (!value) return "-"
    return new Date(value).toLocaleString("en-US", {
      month: "numeric",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
  }

  const formatCurrency = (value: number | null) => {
    if (value == null || value === 0) return null
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value)
  }

  const formatOdometer = (value: number | null) => {
    if (value == null || value === 0) return "?"
    return Math.round(value).toLocaleString()
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`${equipment.display_name} - Case History`} />

      <div className="p-6">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Case History</h1>
          <Link
            href={`/equipment/${equipment.id}`}
            className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
          >
            &larr; {equipment.display_name}
          </Link>
        </div>

        {assignments.length > 0 ? (
          <ul className="space-y-3">
            {assignments.map((assignment) => {
              const hasOdometer =
                equipment.requires_odometer ||
                (assignment.start_odometer != null &&
                  assignment.start_odometer > 0) ||
                (assignment.end_odometer != null && assignment.end_odometer > 0)

              const billFormatted = formatCurrency(assignment.billable_amount)

              return (
                <li key={assignment.id}>
                  <Link
                    href={
                      assignment.case_id
                        ? `/cases/${assignment.case_id}/equipment`
                        : "#"
                    }
                    className="border-hpi-light-gray hover:bg-hpi-lightest-gray block rounded border p-4"
                  >
                    <div className="mb-1 font-medium">
                      {assignment.case_name ?? "Unknown Case"} &mdash;{" "}
                      {assignment.employee_name ?? "Unknown"} on{" "}
                      {formatDateTime(assignment.assigned_at)}
                    </div>

                    <div className="space-y-1 text-sm">
                      {assignment.install_vehicle && (
                        <div>
                          <span className="font-medium">Vehicle:</span>{" "}
                          {assignment.install_vehicle}
                        </div>
                      )}

                      {assignment.install_gps_location && (
                        <div>
                          <span className="font-medium">Device Placement:</span>{" "}
                          {assignment.install_gps_location}
                        </div>
                      )}

                      {assignment.install_vehicle_location && (
                        <div>
                          <span className="font-medium">
                            Location of Vehicle when device was installed:
                          </span>{" "}
                          {assignment.install_vehicle_location}
                        </div>
                      )}

                      {assignment.returned_at && (
                        <div className="mt-2">
                          <span className="font-medium">Returned:</span>{" "}
                          {formatDateTime(assignment.returned_at)}
                          {assignment.removed_by_name && (
                            <span> by {assignment.removed_by_name}</span>
                          )}
                        </div>
                      )}

                      {assignment.remove_vehicle_location && (
                        <div>
                          <span className="font-medium">
                            Location of Vehicle when device removed:
                          </span>{" "}
                          {assignment.remove_vehicle_location}
                        </div>
                      )}

                      {hasOdometer && (
                        <div>
                          <span className="font-medium">Odo/Hours:</span>{" "}
                          {formatOdometer(assignment.start_odometer)}-
                          {formatOdometer(assignment.end_odometer)}
                          {assignment.odometer_usage != null && (
                            <span>
                              {" "}
                              ({Math.round(assignment.odometer_usage)})
                            </span>
                          )}
                        </div>
                      )}

                      {billFormatted && (
                        <div>
                          <span className="font-medium">Bill:</span>{" "}
                          {billFormatted}
                        </div>
                      )}

                      {assignment.notes && (
                        <div className="text-hpi-med-gray">
                          <span className="font-medium">Notes:</span>{" "}
                          {assignment.notes}
                        </div>
                      )}
                    </div>
                  </Link>
                </li>
              )
            })}
          </ul>
        ) : (
          <div className="bg-hpi-lightest-gray text-hpi-med-gray rounded p-4 text-center">
            No Case History found
          </div>
        )}
      </div>
    </AppLayout>
  )
}
